// CommitOverviewPane.tsx — the multiselect commit pane (requirements 5+6).
//
// Reads /overview?experiment=…&sha=…&sha=… for the commits selected in the
// gitgraph and renders them side by side: first the metrics table (one row
// per metric, one column per commit, objectives on top), then the live data
// feeds those same commits reported, then the artifacts their runs uploaded
// (opened side by side in mime-type viewers). Metrics and feeds belong on
// one surface — that is what retiring the separate live-metrics page was for.
//
// Nothing here owns the selection: it arrives as a prop from the URL and
// every change goes back out through onRemove / onClear.
//
// Degradation: a server without /overview yet (A2's route is new) falls back
// to the per-commit /commits/{sha} read, which carries the same values and
// runs one request at a time. If that is missing too the pane says so and
// the rest of the page keeps working.
import { useEffect, useState } from 'react';
import Button from 'react-bootstrap/Button';
import { unwrapJson } from 'common';
import { apiClient } from '../api';
import type { Commit, CommitOverview, OverviewResponse, Run, ValueOnCommit } from '../api';
import { MAX_SELECTION } from './viewState';
import { shortSha, subjectOf } from './format';
import { useLiveTick } from './live';
import OverviewMetricsTable from './OverviewMetricsTable';
import OverviewFeeds from './OverviewFeeds';
import OverviewArtifacts from './OverviewArtifacts';

interface Props {
  owner: string;
  name: string;
  /** Selected experiment slug; '' = repo-wide. */
  experiment: string;
  /** Count descendant experiments' values and feeds, matching the graph. */
  descendants: boolean;
  /** Objective metric names, position-ordered. */
  objectives: string[];
  selection: string[];
  onRemove: (sha: string) => void;
  onClear: () => void;
  /** Bumped when dirty marks change, to refetch. */
  reloadKey: number;
}

// The pre-rework per-commit read, used when /overview is not deployed.
async function fallbackOverview(
  owner: string,
  name: string,
  experiment: string,
  shas: string[],
): Promise<CommitOverview[]> {
  const q = experiment ? `?experiment=${encodeURIComponent(experiment)}` : '';
  const out = await Promise.all(shas.map(async (sha) => {
    const res = await apiClient.fetch(
      `/api/v1/repos/${owner}/${name}/commits/${encodeURIComponent(sha)}${q}`,
    );
    const body = await unwrapJson<{
      commit: Commit;
      values: ValueOnCommit[] | null;
      runs: Run[] | null;
    }>(res);
    return { commit: body.commit, values: body.values ?? [], feeds: body.runs ?? [], artifacts: [] };
  }));
  return out;
}

export default function CommitOverviewPane({
  owner, name, experiment, descendants, objectives, selection, onRemove, onClear,
  reloadKey,
}: Props) {
  const [commits, setCommits] = useState<CommitOverview[]>([]);
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(false);
  const [live, setLive] = useState(true);
  const tick = useLiveTick(live);
  const selKey = selection.join(',');

  useEffect(() => {
    let cancelled = false;
    if (selection.length === 0) {
      setCommits([]);
      setError('');
      return;
    }
    setLoading(true);
    const params = new URLSearchParams();
    if (experiment) params.set('experiment', experiment);
    if (descendants) params.set('descendants', '1');
    for (const sha of selection.slice(0, MAX_SELECTION)) params.append('sha', sha);
    (async () => {
      try {
        const res = await apiClient.fetch(
          `/api/v1/repos/${owner}/${name}/overview?${params.toString()}`,
        );
        const body = await unwrapJson<OverviewResponse>(res);
        if (!cancelled) {
          setCommits(body.commits ?? []);
          setError('');
        }
      } catch (err) {
        // /overview missing or erroring: try the per-commit route before
        // giving up, so the pane degrades in function and not in shape.
        try {
          const rows = await fallbackOverview(
            owner, name, experiment, selection.slice(0, MAX_SELECTION),
          );
          if (!cancelled) {
            setCommits(rows);
            setError('');
          }
        } catch {
          if (!cancelled) {
            setCommits([]);
            setError(err instanceof Error ? err.message : 'Failed to load the selected commits');
          }
        }
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => { cancelled = true; };
    // selKey stands in for selection (a fresh array every render).
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [owner, name, experiment, descendants, selKey, tick, reloadKey]);

  // Keep the column order the user picked, even when the server answers in
  // its own order or resolves a short prefix to a full sha.
  const ordered = selection
    .map((sel) => commits.find((c) => c.commit.sha === sel || c.commit.sha.startsWith(sel)))
    .filter((c): c is CommitOverview => !!c);
  // While a new selection is in flight, show nothing rather than the
  // previous selection's numbers under the new shas' heading.
  const shown = ordered.length > 0 ? ordered : loading ? [] : commits;

  return (
    <div className="card overview-pane">
      <div className="card-header d-flex flex-wrap align-items-center gap-2">
        <strong>Commit Overview</strong>
        <span className="text-secondary">
          {selection.length === 0
            ? 'nothing selected'
            : `${selection.length} selected`}
        </span>
        {shown.map((c) => (
          <span key={c.commit.sha} className="feeds-chip">
            <code>{shortSha(c.commit.sha)}</code>
            <span className="text-secondary text-truncate" style={{ maxWidth: '18rem' }}>
              {subjectOf(c.commit.message)}
            </span>
            <button
              type="button"
              aria-label={`Deselect ${shortSha(c.commit.sha)}`}
              className="btn btn-sm p-0 ms-1"
              style={{ lineHeight: 1, background: 'none', border: 'none', color: 'inherit' }}
              onClick={() => onRemove(c.commit.sha)}
            >
              ×
            </button>
          </span>
        ))}
        {selection.length > 0 && (
          <Button variant="link" size="sm" className="ms-auto" onClick={onClear}>
            Clear
          </Button>
        )}
      </div>
      <div className="card-body">
        {error && <div className="alert alert-danger">{error}</div>}
        {selection.length === 0 ? (
          <div className="empty-state">
            Pick a commit in the graph to see its metrics and feeds.
            Ctrl/cmd/shift-click adds more (up to {MAX_SELECTION}) and the
            tables compare them side by side.
          </div>
        ) : loading && shown.length === 0 ? (
          <div className="text-secondary">Loading…</div>
        ) : shown.length === 0 ? (
          <div className="empty-state">
            None of the selected commits are known to increment yet.
          </div>
        ) : (
          <>
            <h2 className="h6">Metrics</h2>
            <OverviewMetricsTable commits={shown} objectives={objectives} />
            <h2 className="h6 mt-4">Feeds</h2>
            <OverviewFeeds
              owner={owner}
              name={name}
              commits={shown}
              tick={tick + reloadKey}
            />
            <h2 className="h6 mt-4">Artifacts</h2>
            <OverviewArtifacts commits={shown} />
          </>
        )}
      </div>
      {selection.length > 0 && (
        <div className="card-footer d-flex flex-wrap align-items-center gap-3">
          <span className="text-secondary">
            {shown.reduce((n, c) => n + (c.values?.length ?? 0), 0)} values ·{' '}
            {shown.reduce((n, c) => n + (c.feeds?.length ?? 0), 0)} feeds ·{' '}
            {shown.reduce((n, c) => n + (c.artifacts?.length ?? 0), 0)} artifacts
          </span>
          <div className="form-check form-switch ms-auto m-0">
            <input
              id="overview-live"
              className="form-check-input"
              type="checkbox"
              checked={live}
              onChange={(e) => setLive(e.target.checked)}
            />
            <label htmlFor="overview-live" className="form-check-label text-secondary">
              Live
            </label>
          </div>
        </div>
      )}
    </div>
  );
}
